import './GenerateMatrixModalContent.css';

import { CSSProperties, useEffect, useState } from 'react';
import Button from '../../Button/Button';
import Modal from '../Modal/Modal';
import ModalBody from '../ControlActionModalContent/components/ModalBody/ModalBody';
import MatrixOverlapModalContent from '../MatrixOverlapModalContent/MatrixOverlapModalContent';
import SeptimLoadingSpinner from '../../Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import SettingsPanel from '../../Manager/LayoutEditorContent/components/SettingsPanel/SettingsPanel';
import { useFullscreenReferenceNavigation } from '../../Manager/LayoutEditorContent/hooks/useFullscreenReferenceNavigation';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useLayoutEditorContext } from '../../../hooks/context/useLayoutEditorContext';
import { useLayoutResolveData } from '../../Manager/LayoutEditorContent/hooks/useLayoutResolveData';
import { useModalKeyboard } from '../../../hooks/shortcuts/useModalKeyboard';
import { useResolvedItemsVisibility } from '../../../hooks/useResolvedItemsVisibility';
import { useLayoutFormColumns } from '../../../hooks/useLayoutFormColumns';
import { applyLayoutObjectDefaults } from '../../../utils/layouts/applyLayoutObjectDefaults';
import { getMatrixTemplateDefaults } from '../../../utils/layouts/getMatrixTemplateDefaults';
import { getMatrixOverlappingObjects } from '../../../utils/layouts/getMatrixOverlappingObjects';
import { useLayoutObjectTemplateContext } from '../../../hooks/context/useLayoutObjectTemplateContext';
import { isEmptyValue } from '../../../utils/managerUtils';
import { FullscreenReferenceType, JSONObject } from '../../../types/types';

// "Generate matrix" dialog: renders the definition's matrixForm and submits
// the values to the matrix generate API. The form state lives in
// LayoutEditorContext — a fullscreen reference opened from a field navigates
// to the editor's reference route (same flow as the side panel), which
// unmounts this dialog; on return the surviving context state reopens it.
const GenerateMatrixModalContent = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOverlapConfirmOpen, setIsOverlapConfirmOpen] = useState(false);
  const { t } = useTranslate();
  // Page-wide reference dataset cache (shared with the fullscreen reference route).
  const { referenceDatasets, setReferenceDatasets } = useDatasetContext();
  const {
    definition,
    objects,
    matrixFormData,
    fetchMatrixDefaults,
    generateMatrix,
    closeMatrixDialog,
    seedMatrixFormData,
    patchMatrixFormData,
  } = useLayoutEditorContext();
  const { openFullscreenReferenceRoute } = useFullscreenReferenceNavigation();
  // The dialog portals to document.body, so the editor panel's --form-columns
  // never reaches it — measure the dialog form and apply the same breakpoints.
  const { measureRef, columns } = useLayoutFormColumns();

  const isSeeded = matrixFormData !== null;
  const { template } = useLayoutObjectTemplateContext();
  const visibilityResolveData = useLayoutResolveData(matrixFormData ?? {});
  const { visibleByName, pending: isVisibilityPending } =
    useResolvedItemsVisibility(
      definition?.matrixForm?.items ?? [],
      visibilityResolveData
    );

  // Confirm stays blocked while a mandatory matrixForm field is empty (the
  // request would 400 anyway — e.g. the required recipe group). Fields hidden
  // by their `visible` expression don't count — the user has no input to fill
  // (dataset forms validate only visible fields too).
  const hasEmptyMandatoryField = !!definition?.matrixForm?.items.some(
    (item) =>
      item.mandatory &&
      item.name &&
      item.allowVisible !== false &&
      visibleByName[item.name] !== false &&
      isEmptyValue(matrixFormData?.[item.name])
  );
  const canConfirm =
    isSeeded && !isVisibilityPending && !hasEmptyMandatoryField;

  const submitMatrix = async (clearExisting: boolean): Promise<void> => {
    if (isSubmitting) {
      return;
    }

    setIsOverlapConfirmOpen(false);
    setIsSubmitting(true);
    try {
      // Errors are toasted by the shared api error layer; the dialog stays
      // open so the user can adjust the values.
      const isGenerated = await generateMatrix(
        matrixFormData ?? {},
        clearExisting
      );
      if (isGenerated) {
        closeMatrixDialog();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Old-Manager parity: objects lying under the space the generated matrix
  // will take ask first whether to delete them (only those — the rest of the
  // canvas survives either choice); a clear space generates right away.
  const handleConfirm = (): void => {
    if (isSubmitting) {
      return;
    }

    const overlappingObjects = getMatrixOverlappingObjects(
      matrixFormData ?? {},
      objects
    );
    if (overlappingObjects.length > 0) {
      setIsOverlapConfirmOpen(true);
      return;
    }

    void submitMatrix(false);
  };

  // Same flow as the side panel fields: picker state into the context, then
  // the reference route. The dialog unmounts and reopens on the way back.
  const handleOpenFullscreenReference = (
    reference: FullscreenReferenceType
  ): void => {
    openFullscreenReferenceRoute(reference, 'matrix');
  };

  useModalKeyboard({
    onEnter: canConfirm && !isSubmitting ? handleConfirm : undefined,
    onEscape: closeMatrixDialog,
  });

  // Server defaults arrive before the form shows, so the user never races a
  // late pre-fill overwrite. Default priority per field: the last selected
  // object (template) → the matrixDefault response → the definition defaults
  // (control/item `default`). Skipped when the data survived a reference
  // round-trip; a failed fetch opens the form without the server defaults.
  useEffect(() => {
    if (isSeeded) {
      return;
    }

    let isActive = true;
    const templateDefaults = getMatrixTemplateDefaults(
      template,
      definition?.matrixForm
    );

    fetchMatrixDefaults()
      .then((defaults) => {
        if (isActive) {
          seedMatrixFormData(
            applyLayoutObjectDefaults(
              { ...(defaults ?? {}), ...templateDefaults },
              definition?.matrixForm
            )
          );
        }
      })
      .catch(() => {
        if (isActive) {
          seedMatrixFormData(
            applyLayoutObjectDefaults(templateDefaults, definition?.matrixForm)
          );
        }
      });

    return () => {
      isActive = false;
    };
  }, [isSeeded]);

  if (!definition?.matrixForm) {
    return null;
  }

  return (
    <>
      <ModalBody
        header={t('layoutEditor.generateMatrix')}
        className="generate-matrix-modal"
        text={
          isSeeded ? (
            // Column count from the dialog's own width (0 until the first
            // measurement — the item CSS falls back to 12 for that frame).
            <div
              ref={measureRef}
              className="generate-matrix-modal-form"
              style={
                columns
                  ? ({ ['--form-columns']: columns } as CSSProperties)
                  : undefined
              }
            >
              <SettingsPanel
                form={definition.matrixForm}
                data={matrixFormData as JSONObject}
                referenceDatasets={referenceDatasets}
                setReferenceDatasets={setReferenceDatasets}
                handlerScope="matrix"
                onFieldChange={patchMatrixFormData}
                onFieldDefault={patchMatrixFormData}
                onOpenFullscreenReference={handleOpenFullscreenReference}
              />
            </div>
          ) : (
            <SeptimLoadingSpinner className="generate-matrix-modal-spinner" />
          )
        }
        buttons={
          isSubmitting ? (
            <SeptimLoadingSpinner className="generate-matrix-modal-spinner" />
          ) : (
            <>
              <Button
                color="primary"
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                {t('control.modal.confirm')}
              </Button>
              <Button color="light" fill="solid" onClick={closeMatrixDialog}>
                {t('cancel')}
              </Button>
            </>
          )
        }
      />
      {/* Asked before the generate request: keep or delete the objects the
          matrix would overlap. Backdrop/Escape backs out without generating. */}
      {isOverlapConfirmOpen && (
        <Modal
          isOpen
          isUnclosable={false}
          onClose={() => setIsOverlapConfirmOpen(false)}
          content={
            <MatrixOverlapModalContent
              onKeep={() => void submitMatrix(false)}
              onClear={() => void submitMatrix(true)}
              onCancel={() => setIsOverlapConfirmOpen(false)}
            />
          }
        />
      )}
    </>
  );
};

export default GenerateMatrixModalContent;
